// MEMORY surface (N-MEM-1..4), the "Signal" re-layout (Memory Screen v2): the knowledge-tree rail beside the
// article reader, with the three review surfaces (Always in context / Update proposals / Conflicts) as
// stacked decision cards — over the SAME `memory` (C) read projections and the SAME decision routes as
// before. The dashboard is DECISION-ROUTING ONLY: it renders the read projections and routes a human decision
// (accept/reject a Proposal, resolve a Conflict) to C — it NEVER edits Memory and sets no approval field
// (P-GATE). No new approval step, pending state or confirmation queue was added. Tokens only (G-181); every
// displayed state derives from the owning lifecycle (G-182).
//
// The rail sits OUTSIDE the load frame on purpose: the tree is assembled from levels already read, so
// navigating to a Node reloads the article column alone and the tree keeps its place.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useShell } from '../app/ShellContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { MemoryTreeRail } from './components/MemoryTreeRail.tsx';
import { MemoryArticle } from './components/MemoryArticle.tsx';
import { MemoryConnectionMap, memoryEdges } from './components/MemoryConnectionMap.tsx';
import type { MemoryEdge } from './components/MemoryConnectionMap.tsx';
import { MemoryReview } from './components/MemoryReview.tsx';
import type { ReviewTab } from './components/MemoryReview.tsx';
import {
  ROOT_AREA,
  ancestorAreas,
  areaOfPath,
  flattenMemoryTree,
  useMemoryBrowse,
} from './components/memory-tree.ts';
import { useMemorySearch } from './components/useMemorySearch.ts';
import {
  ScreenToolbar,
  ScreenBody,
  ScreenSubTabs,
  TbInfo,
  TbStamp,
} from '../app/screen-chrome.tsx';
import type { ScreenSubTab } from '../app/screen-chrome.tsx';
import { Panel, EmptyState } from './primitives.tsx';
import type {
  MemoryBrowserProjection,
  MemoryReviewProjection,
  MemorySearchResultView,
} from './know-projections.ts';

/** The Memory surface tabs — the wiki browser + the three decision-routing review lists. */
type MemoryTab = 'browse' | ReviewTab;

/** The Memory surface. */
export function MemorySurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const shell = useShell();
  const [tab, setTab] = useState<MemoryTab>('browse');

  // A chat memory-trace click (BUILD-DEC-098) lands on the Browse tab so the focused node is shown; the
  // browse pane then applies + clears the focus. Force the tab whenever a focus is pending. A review card's
  // "open the memory" link uses the SAME mechanism, so it lands the reader on the Node it names.
  const hasMemoryFocus = shell.memoryFocus !== null;
  useEffect(() => {
    if (hasMemoryFocus) setTab('browse');
  }, [hasMemoryFocus]);

  // The subtab badges come from the review projection so a count is honest (not a fixed number).
  const reviewState = useProjection<MemoryReviewProjection>(api.getMemoryReview);
  const review = reviewState.data;
  const tabs: readonly ScreenSubTab[] = [
    { id: 'browse', label: 'Browse' },
    { id: 'promoted', label: 'Always in context', badge: review?.promoted.length },
    { id: 'proposals', label: 'Update proposals', badge: review?.proposals.length },
    { id: 'conflicts', label: 'Conflicts', badge: review?.conflicts.length },
  ];

  return (
    <>
      <ScreenToolbar title="Memory">
        {review !== null && review.promoted.length > 0 ? (
          <TbStamp>{review.promoted.length} always in context</TbStamp>
        ) : null}
        <TbInfo
          title="Memory"
          tip="Read and decide — never raw-edit. A navigable wiki of areas with first-class links; every citation resolves to the exact node."
        />
      </ScreenToolbar>
      <ScreenSubTabs
        label="Memory sections"
        tabs={tabs}
        active={tab}
        onSelect={(id) => setTab(id as MemoryTab)}
      />
      <ScreenBody>
        {tab === 'browse' ? <MemoryBrowsePane /> : <MemoryReviewPane tab={tab} />}
      </ScreenBody>
    </>
  );
}

/** Where the browser is pointed: the area whose level was last read, and the focused Node. */
interface BrowsePosition {
  readonly area: string;
  readonly nodeId: string | undefined;
  readonly setArea: (area: string) => void;
  readonly setNodeId: (nodeId: string | undefined) => void;
}

/**
 * The browser's position, initialised from a pending chat memory-trace focus (BUILD-DEC-098) and re-pointed
 * when one lands while the pane is already mounted (the usual case — the chat dock is open over another
 * surface). The focus is CONSUMED once applied so it cannot re-fire.
 */
function useBrowsePosition(): BrowsePosition {
  const shell = useShell();
  const [area, setArea] = useState<string>(shell.memoryFocus?.areaPath ?? ROOT_AREA);
  const [nodeId, setNodeId] = useState<string | undefined>(shell.memoryFocus?.nodeId);
  const clearMemoryFocus = shell.clearMemoryFocus;
  const memoryFocus = shell.memoryFocus;
  useEffect(() => {
    if (memoryFocus === null) return;
    setArea(memoryFocus.areaPath);
    setNodeId(memoryFocus.nodeId);
    clearMemoryFocus();
  }, [memoryFocus, clearMemoryFocus]);
  return { area, nodeId, setArea, setNodeId };
}

/** Which areas the reader has open, plus the two things that change that: their own toggle, and a focused
 *  Node revealing itself. A Node reached from chat or from a review card would otherwise sit in a shut
 *  branch, so its ancestors open and its (unread) level is fetched. */
function useOpenAreas(
  levels: ReturnType<typeof useMemoryBrowse>['levels'],
  nodePath: string | undefined,
  setArea: (area: string) => void,
): { readonly openAreas: ReadonlySet<string>; readonly onToggleArea: (areaPath: string) => void } {
  const [openAreas, setOpenAreas] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (nodePath === undefined) return;
    const nodeArea = areaOfPath(nodePath);
    if (nodeArea === ROOT_AREA) return;
    setOpenAreas((previous) => new Set([...previous, ...ancestorAreas(nodeArea)]));
    if (levels[nodeArea] === undefined) setArea(nodeArea);
  }, [nodePath, levels, setArea]);
  const onToggleArea = useCallback(
    (areaPath: string) => {
      setOpenAreas((previous) => {
        const next = new Set(previous);
        if (next.has(areaPath)) next.delete(areaPath);
        else next.add(areaPath);
        return next;
      });
      // Opening an area the browser has never read is what fetches that level — nothing is assumed about it.
      if (levels[areaPath] === undefined) setArea(areaPath);
    },
    [levels, setArea],
  );
  return { openAreas, onToggleArea };
}

/** The rail's diagram slot — the focused Node's real one-hop neighbourhood, or nothing at all. */
function ConnectionSlot({
  title,
  edges,
}: {
  readonly title: string;
  readonly edges: readonly MemoryEdge[];
}): React.JSX.Element {
  return (
    <div className="mw-map">
      <MemoryConnectionMap title={title} edges={edges} />
      <div className="mw-map-cap">
        <span className="mw-map-dot" aria-hidden="true" />
        <span className="name">{title}</span>
        <span className="cnt">
          {edges.length} {edges.length === 1 ? 'link' : 'links'}
        </span>
      </div>
    </div>
  );
}

/** The reader column — the loaded article, or the honest "nothing focused yet" line. */
function BrowseReader({
  browser,
  onFocusNode,
}: {
  readonly browser: MemoryBrowserProjection;
  readonly onFocusNode: (nodeId: string) => void;
}): React.JSX.Element {
  const shell = useShell();
  const node = browser.node;
  if (node === null) {
    return (
      <Panel>
        <EmptyState>Choose a memory from the tree to read it.</EmptyState>
      </Panel>
    );
  }
  return (
    <MemoryArticle
      node={node}
      edges={memoryEdges(node, browser.backlinks)}
      onFocusNode={onFocusNode}
      onAsk={() => shell.openNewChatWithDraft(`About the memory "${node.frontmatter.title}": `)}
    />
  );
}

/** The browse pane — the knowledge-tree rail beside the article reader. */
function MemoryBrowsePane(): React.JSX.Element {
  const { area, nodeId, setArea, setNodeId } = useBrowsePosition();
  const [query, setQuery] = useState('');
  const { projection, levels } = useMemoryBrowse(area, nodeId);
  const search = useMemorySearch(query);
  const node = projection.data?.node ?? null;
  const { openAreas, onToggleArea } = useOpenAreas(levels, node?.path, setArea);
  const onFocusNode = useCallback((next: string) => setNodeId(next), [setNodeId]);
  const onFocusSearchResult = useCallback(
    (result: MemorySearchResultView) => {
      // Open exactly the selected article's own area. No ancestor branch is fetched just to reveal it.
      setArea(areaOfPath(result.path));
      setNodeId(result.id);
    },
    [setArea, setNodeId],
  );

  const rows = useMemo(
    () =>
      flattenMemoryTree({
        levels,
        openAreas,
        focusNodeId: nodeId,
      }),
    [levels, openAreas, nodeId],
  );
  const edges = node !== null ? memoryEdges(node, projection.data?.backlinks ?? []) : [];

  return (
    <div className="mw-cols" data-memory-browser>
      <MemoryTreeRail
        rows={rows}
        loaded={Object.keys(levels).length > 0}
        focusedNodeId={nodeId}
        query={query}
        onQuery={setQuery}
        search={search}
        onToggleArea={onToggleArea}
        onFocusNode={onFocusNode}
        onFocusSearchResult={onFocusSearchResult}
      >
        {node !== null && edges.length > 0 ? (
          <ConnectionSlot title={node.frontmatter.title} edges={edges} />
        ) : null}
      </MemoryTreeRail>
      <SurfaceFrame state={projection}>
        {(browser) => <BrowseReader browser={browser} onFocusNode={onFocusNode} />}
      </SurfaceFrame>
    </div>
  );
}

/** The honest refusal line for a decision that did not go through (deep-sweep 2026-07-16 N4: a refused
 *  accept/reject previously surfaced NOTHING — the silent-failure family the other surfaces already fixed).
 *  The Memory-decision Gate is capability-gated (memory.decideProposal / memory.resolveConflict — operator
 *  only by design), and the projection carries no per-viewer capability flag, so the client cannot know ahead
 *  of the route whether this account may decide. On a refusal we say so honestly AND latch the controls
 *  read-only (see MemoryReviewPane) so we never keep offering an active control that only 403s again. */
function decisionNote(serverNote: string | undefined): string {
  const reason =
    serverNote !== undefined && serverNote.length > 0
      ? serverNote
      : 'only an Operator can decide these';
  return `The decision was not recorded — ${reason}. Nothing was changed.`;
}

/**
 * The Memory-decision concern for a review pane: the refusal note (kept outside SurfaceFrame so a projection
 * reload cannot unmount it) + the read-only latch (F6 — the decide capability is operator-only; once refused
 * we stop offering Accept/Reject/Resolve that would only 403 again) + both decision handlers, which settle
 * identically (success reloads; refusal latches the note + read-only state).
 */
function useMemoryDecisions(reload: () => void) {
  const api = useSurfaceApi();
  const [note, setNote] = useState<string | null>(null);
  const [decideRefused, setDecideRefused] = useState(false);
  const settle = useCallback(
    (result: Awaited<ReturnType<typeof api.routeMemoryDecision>>) => {
      if (result.ok) reload();
      else {
        setNote(decisionNote(result.note));
        setDecideRefused(true);
      }
      return result;
    },
    [api, reload],
  );
  const onDecideProposal = useCallback(
    async (proposalId: string, decision: 'accepted' | 'rejected') => {
      setNote(null);
      return settle(await api.routeMemoryDecision('proposal_decide', proposalId, decision));
    },
    [api, settle],
  );
  const onResolveConflict = useCallback(
    async (conflictId: string) => {
      setNote(null);
      return settle(await api.routeMemoryDecision('conflict_resolve', conflictId, 'accepted'));
    },
    [api, settle],
  );
  return { note, decideRefused, onDecideProposal, onResolveConflict };
}

/** A review pane (proposals / conflicts / promoted) — renders the list + routes decisions to C. */
function MemoryReviewPane({ tab }: { readonly tab: ReviewTab }): React.JSX.Element {
  const api = useSurfaceApi();
  const shell = useShell();
  const state = useProjection<MemoryReviewProjection>(api.getMemoryReview);
  const { note, decideRefused, onDecideProposal, onResolveConflict } = useMemoryDecisions(
    state.reload,
  );
  // Opening the memory a card names re-uses the SAME deep-focus mechanism a chat memory trace uses
  // (BUILD-DEC-098): the surface switches to Browse and the reader lands on that Node.
  const openMemoryNode = shell.openMemoryNode;
  const onOpenNode = useCallback((nodeId: string) => openMemoryNode(nodeId), [openMemoryNode]);

  return (
    <>
      {note !== null ? (
        <p role="status" className="gate-note" data-memory-decision-note>
          {note}
        </p>
      ) : null}
      <SurfaceFrame state={state}>
        {(review) => (
          <MemoryReview
            tab={tab}
            review={review}
            onDecideProposal={onDecideProposal}
            onResolveConflict={onResolveConflict}
            onOpenNode={onOpenNode}
            decideDisabled={decideRefused}
          />
        )}
      </SurfaceFrame>
    </>
  );
}
